import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';

import {
  createContactInteraction,
  deleteContactInteraction,
  getContactInteractionsQueryKey,
  listContactInteractions,
  updateContactInteraction,
  type ContactInteractionItem,
  type CreateInteractionInput,
  type UpdateInteractionInput,
} from '../api/contacts.client.js';

export function useContactInteractions(contactId: string) {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';

  return useQuery({
    queryKey: getContactInteractionsQueryKey(workspaceId, contactId),
    queryFn: () => listContactInteractions(contactId),
    enabled: scope !== null && contactId.length > 0,
  });
}

/**
 * Карточка контакта отдаёт последние взаимодействия вместе с самим контактом,
 * поэтому обновляем весь префикс, а не только список. История контакта тоже
 * меняется: правка и удаление пишутся в аудит.
 */
function useInvalidateInteractions(): () => Promise<void> {
  const queryClient = useQueryClient();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['contacts', workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
    ]);
  };
}

export function useCreateInteractionMutation(contactId: string) {
  const invalidate = useInvalidateInteractions();

  return useMutation<ContactInteractionItem, Error, CreateInteractionInput>({
    mutationFn: (input) => createContactInteraction(contactId, input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateInteractionMutation(contactId: string) {
  const invalidate = useInvalidateInteractions();

  return useMutation<ContactInteractionItem, Error, { id: string; input: UpdateInteractionInput }>({
    mutationFn: ({ id, input }) => updateContactInteraction(contactId, id, input),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteInteractionMutation(contactId: string) {
  const invalidate = useInvalidateInteractions();

  return useMutation<void, Error, string>({
    mutationFn: (interactionId) => deleteContactInteraction(contactId, interactionId),
    onSuccess: () => invalidate(),
  });
}
